import React, { useMemo, useState } from "react";
import { listDistrictsNotReviewed } from "../dpatAssessmentWorkflowStats";
import { ASSESSMENT_STATUS_CHART_COLORS } from "../dpatChartTheme";

function statusBadgeClass(status) {
  switch (status) {
    case "Start":
      return "bg-slate-100 text-[#16325a] border-[#16325a]/20";
    case "Pending":
      return "bg-amber-50 text-amber-800 border-amber-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

const DpatNotReviewedDistrictsTable = ({ rows = [], loading, reportingYear }) => {
  const [search, setSearch] = useState("");
  const districts = useMemo(() => listDistrictsNotReviewed(rows), [rows]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return districts;
    return districts.filter((row) =>
      `${row.districtName} ${row.regionName} ${row.status}`.toLowerCase().includes(q),
    );
  }, [districts, search]);

  return (
    <section className="rounded-xl border border-border bg-card p-4 shadow-sm md:p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">Districts not Reviewed</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {reportingYear} — {districts.length} district{districts.length === 1 ? "" : "s"} have
            not reached Reviewed status
          </p>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search district, region, or status"
          className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm text-foreground outline-none ring-[#16325a]/20 focus:ring-2 sm:w-72"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-3 font-semibold">No.</th>
              <th className="px-3 py-3 font-semibold">District</th>
              <th className="px-3 py-3 font-semibold">Region</th>
              <th className="px-3 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">
                  Loading districts…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">
                  {districts.length === 0
                    ? "All districts have reached Reviewed status."
                    : "No districts match this search."}
                </td>
              </tr>
            ) : (
              filtered.map((row, index) => (
                <tr key={row.districtId || `${row.districtName}-${index}`} className="border-b border-border last:border-0">
                  <td className="px-3 py-3 tabular-nums text-muted-foreground">{index + 1}</td>
                  <td className="px-3 py-3 font-medium text-foreground">{row.districtName}</td>
                  <td className="px-3 py-3 text-foreground">{row.regionName}</td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
                      style={{
                        borderColor: `${ASSESSMENT_STATUS_CHART_COLORS[row.status] || ASSESSMENT_STATUS_CHART_COLORS.none}33`,
                      }}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default DpatNotReviewedDistrictsTable;
